import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import {
  HOOK,
  PROBLEM,
  INTUITION,
  APPROACH,
  CODE,
  LANGS,
  PAYOFF,
  OUTRO,
} from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1500,
    }}
  >
    {children}
  </div>
);

/* 01 — HOOK : fixed-size slicing shreds a function */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  // the "2048" cut line sweeps across the code block
  const cut = interpolate(frame, [30, 70], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="ingest-time chunking · what breaks" color={COLORS.cyan} />
          <Big>
            Fixed-size chunks are
            <br />
            <span style={{ color: COLORS.red, opacity: 0.4 + cut * 0.6 }}>
              shredding
            </span>{" "}
            your RAG
          </Big>
          <div
            style={{
              fontFamily: FONTS.mono,
              fontSize: 26,
              color: COLORS.dim,
              marginTop: 34,
            }}
          >
            cut every <span style={{ color: COLORS.amber }}>2048 chars</span> · meaning ignored
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 02 — PROBLEM : a function split mid-body */
export const Scene02Problem: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.problem.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="the silent failure" color={COLORS.red} />
        <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
          <Panel accent="red" title="chunk A · bytes 0–2048" width={500}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 21, lineHeight: 1.85, color: COLORS.text }}>
              <div><span style={{ color: COLORS.cyan }}>function</span> handleAuth() {"{"}</div>
              <div>  const token = sign(</div>
              <div>    user, sessionKey,</div>
              <div style={{ color: COLORS.red }}>  ── cut at 2048 ──</div>
            </div>
          </Panel>
          <Panel accent="red" title="chunk B · bytes 2048–…" width={500}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 21, lineHeight: 1.85, color: COLORS.text }}>
              <div>      expiry, claims)</div>
              <div>  return token</div>
              <div>{"}"}</div>
              <div style={{ color: COLORS.dim }}>…neither half is the function</div>
            </div>
          </Panel>
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={PROBLEM} accent="red" />
  </AbsoluteFill>
);

/* 03 — INTUITION : cut at the seams */
export const Scene03Intuition: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.intuition.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="intuition" color={COLORS.violet} />
        <Big size={70}>
          Don&apos;t cut every N bytes.
          <br />
          <span style={{ color: COLORS.violet }}>Cut at the seams.</span>
        </Big>
        <div style={{ display: "flex", gap: 26, marginTop: 40 }}>
          {[
            { k: "code", v: "functions / classes", c: COLORS.cyan },
            { k: "markdown", v: "headings", c: COLORS.green },
            { k: "table", v: "rows", c: COLORS.lime },
          ].map((s) => (
            <div
              key={s.k}
              style={{
                fontFamily: FONTS.mono,
                fontSize: 22,
                color: COLORS.text,
                border: `1px solid ${s.c}66`,
                borderRadius: 8,
                padding: "12px 18px",
                background: COLORS.panel,
              }}
            >
              <span style={{ color: s.c }}>{s.k}</span> → {s.v}
            </div>
          ))}
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={INTUITION} accent="violet" />
  </AbsoluteFill>
);

/* 04 — APPROACH : read type, then dispatch */
const PIPE: PipeNode[] = [
  { label: "input", value: "DOC", sub: "raw text", accent: "cyan" },
  { label: "detect", value: "contentType", sub: "code/md/table/general", accent: "violet" },
  { label: "dispatch", value: "switch", sub: "per-type strategy", accent: "green" },
  { label: "output", value: "CHUNKS", sub: "type · title · loc", accent: "amber" },
];
export const Scene04Approach: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.approach.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 80px" }}>
      <Kicker text="adaptiveChunkStructured() · src/lib/rag" color={COLORS.green} />
      <div style={{ width: 1680 }}>
        <PipelineGraph nodes={PIPE} startFrame={6} stepFrames={22} nodeWidth={230} />
      </div>
      <div
        style={{
          fontFamily: FONTS.body,
          fontSize: 26,
          color: COLORS.dim,
          marginTop: 38,
        }}
      >
        fixed-size is the <span style={{ color: COLORS.amber }}>fallback</span>, not the default
      </div>
    </AbsoluteFill>
    <Caption lines={APPROACH} accent="green" />
  </AbsoluteFill>
);

/* 05 — CODE : the literal switch + tree-sitter delegation */
const SRC = `// src/lib/rag/adaptive-chunker.ts
switch (contentType) {
  case 'code':                       // AST boundaries
    return chunkCodeStructured(text, language, filePath)
  case 'markdown':                   // split at headings  #{1,4}
    return chunkMarkdownStructured(text, filePath)
  case 'table':                      // keep header, split by rows
    return chunkTableStructured(text, filePath)
  case 'general':
  default:                           // RecursiveCharacterTextSplitter
    return chunkGeneralStructured(text, filePath)
}

// code path delegates to tree-sitter for real AST nodes:
const boundaries = await getTopLevelBoundaries(code, filePath)
// regex chunker only if tree-sitter can't parse the file`;
export const Scene05Code: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.code.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 40 }}>
      <CodePanel
        code={SRC}
        language="tsx"
        filename="adaptive-chunker.ts"
        highlightLines={[3, 16]}
        revealFrames={240}
        fontSize={22}
        width={1320}
      />
    </AbsoluteFill>
    <Caption lines={CODE} accent="green" />
  </AbsoluteFill>
);

/* 06 — LANGS : 13 grammars + comment attachment + no-LLM honesty */
export const Scene06Langs: React.FC = () => {
  const frame = useCurrentFrame();
  const c1 = interpolate(frame, [110, 140], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.langs.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="tree-sitter-parser.ts · EXTRACTORS dispatch" color={COLORS.cyan} />
        <div style={{ display: "flex", alignItems: "baseline", gap: 18 }}>
          <MetricCounter value={13} from={0} startFrame={10} durationFrames={45} accent="cyan" fontSize={150} />
          <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 56, color: COLORS.text }}>
            grammars
          </span>
        </div>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 19,
            color: COLORS.dim,
            marginTop: 10,
            maxWidth: 1200,
            textAlign: "center",
            lineHeight: 1.6,
          }}
        >
          ts · tsx · js · python · go · rust · java · kotlin · c · cpp · c# · ruby · php
        </div>
        <div style={{ display: "flex", gap: 28, marginTop: 40 }}>
          <Callout label="honesty · stale comment" accent="amber" width={420}>
            file header still reads <b style={{ color: COLORS.text }}>“10 Language Support”</b> — the dispatch table has 13
          </Callout>
          <div style={{ opacity: c1 }}>
            <Callout label="comments attach" accent="green" width={420}>
              a doc comment within 2 lines of a function is <b style={{ color: COLORS.text }}>merged into its chunk</b>
            </Callout>
          </div>
        </div>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 22,
            letterSpacing: "0.12em",
            color: COLORS.green,
            marginTop: 34,
          }}
        >
          pure AST + regex · NO LLM calls in chunking
        </div>
      </AbsoluteFill>
      <Caption lines={LANGS} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 07 — PAYOFF : target size + structural output */
export const Scene07Payoff: React.FC = () => {
  const frame = useCurrentFrame();
  const c2 = interpolate(frame, [120, 150], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.payoff.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="every strategy, one target" color={COLORS.lime} />
        <div style={{ display: "flex", alignItems: "baseline", gap: 16 }}>
          <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 56, color: COLORS.dim }}>~</span>
          <MetricCounter value={512} from={0} startFrame={10} durationFrames={55} accent="lime" fontSize={150} />
          <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 56, color: COLORS.text }}>
            tokens / chunk
          </span>
        </div>
        <div style={{ fontFamily: FONTS.mono, fontSize: 20, color: COLORS.dim, marginTop: 8 }}>
          merge the tiny · split the huge · 2048-char target, 4096 hard cap
        </div>
        <div style={{ display: "flex", gap: 30, marginTop: 40 }}>
          <Callout label="structural output" accent="green" width={420}>
            each chunk keeps its <b style={{ color: COLORS.text }}>type, title &amp; file location</b>
          </Callout>
          <div style={{ opacity: c2 }}>
            <Callout label="discoverable" accent="lime" width={420}>
              a function chunk is <b style={{ color: COLORS.text }}>the whole function</b> — embeddable, retrievable
            </Callout>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={PAYOFF} accent="lime" />
    </AbsoluteFill>
  );
};

/* 08 — OUTRO */
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={64}>
          Retrieval can only rank
          <br />
          what ingestion preserved.
          <br />
          <span style={{ color: COLORS.cyan }}>Chunk on structure.</span>
        </Big>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 24,
            letterSpacing: "0.24em",
            color: COLORS.dim,
            marginTop: 44,
          }}
        >
          EAKC · grounded retrieval · build-in-public
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
